import { useId } from "react";
import { useChantierSources } from "../hooks/useChantierSources";
import { useTenantSettings } from "../hooks/useTenantSettings";
import { DEFAULT_TIMEZONE, dayOf } from "../lib/billing";
import {
  BILAN_WEEKS,
  bilanSeries,
  buildProjectBilan,
  buildTeamChantierWeeks,
  dateLabel,
  daysBetween,
  formatQuarters,
  isoWeek,
  lineName,
  OTHERS_KEY,
  prUrl,
  recentMondays,
  shadowRanges,
  weekSegments,
  type BilanSeries,
  type BilanWeek,
  type ChantierPerson,
  type ChantierPr,
} from "../lib/chantiers";

function PrLinks({ prs }: { prs: ChantierPr[] }) {
  return (
    <>
      {prs.map((pr) => (
        <a
          key={`${pr.repo}#${pr.number}`}
          className="chantiers-pr"
          href={prUrl(pr)}
          target="_blank"
          rel="noopener noreferrer"
          title={`${pr.repo}#${pr.number}`}
        >
          #{pr.number}
        </a>
      ))}
    </>
  );
}

function whoText(people: ChantierPerson[]): string {
  return people
    .filter((p) => p.quarters > 0 && p.name)
    .map((p) => p.name)
    .join(", ");
}

function barTitle(
  week: BilanWeek,
  segments: ReturnType<typeof weekSegments>,
): string {
  return [
    `${week.label} : ${formatQuarters(week.quarters)}`,
    ...segments.map((s) => `${s.series.name} ${formatQuarters(s.quarters)}`),
  ].join("\n");
}

function WeekChart({
  weeks,
  series,
  total,
}: {
  weeks: BilanWeek[];
  series: BilanSeries[];
  total: number;
}) {
  const max = Math.max(...weeks.map((w) => w.quarters), 1);
  const color = (key: string) =>
    key === OTHERS_KEY
      ? "bilan-other"
      : `bilan-c${series.findIndex((s) => s.key === key)}`;
  return (
    <figure className="bilan-chart">
      <div
        className="bilan-bars"
        role="img"
        aria-label={`Heures par semaine sur ${weeks.length} semaines, ${formatQuarters(total)} au total. Détail par semaine dans le tableau ci-dessous.`}
      >
        {weeks.map((w) => {
          const segments = weekSegments(w, series);
          return (
            <div
              key={w.monday}
              className="bilan-col"
              title={barTitle(w, segments)}
            >
              <div className="bilan-stack">
                {segments.map((s) => (
                  <div
                    key={s.series.key}
                    className={`bilan-seg ${color(s.series.key)}`}
                    style={{ height: `${(s.quarters / max) * 100}%` }}
                  />
                ))}
              </div>
              <span className="bilan-x">S{isoWeek(w.monday)}</span>
            </div>
          );
        })}
      </div>
      <figcaption>
        <ul className="bilan-legend">
          {series.map((s) => (
            <li key={s.key}>
              <span
                className={`bilan-swatch ${color(s.key)}`}
                aria-hidden="true"
              />
              {s.name}
            </li>
          ))}
        </ul>
      </figcaption>
    </figure>
  );
}

/** Bilan du projet sur les 12 dernières semaines : heures par semaine et par
 * chantier, chantiers cumulés, détail par semaine. Même source et mêmes droits
 * que l'onglet Chantiers : un salarié voit ses heures, un manager ou un admin
 * celles de son équipe. */
export function ProjectBilan({ projectId }: { projectId: string }) {
  const titleId = useId();
  const settings = useTenantSettings();
  const timeZone = settings.data?.timezone || DEFAULT_TIMEZONE;
  const today = dayOf(new Date(), timeZone);
  const mondays = recentMondays(today);
  const { teamView, sources, unreadable, loading, failed, teamFailed, retry } =
    useChantierSources(shadowRanges(mondays[0]!, today));
  const bilan = buildProjectBilan(
    buildTeamChantierWeeks(sources),
    projectId,
    mondays,
    today,
  );
  const series = bilanSeries(bilan);
  const recent = [...bilan.weeks].reverse().filter((w) => w.quarters > 0);

  return (
    <section className="project-bilan" aria-labelledby={titleId}>
      <h2 id={titleId} className="section-label">
        Bilan
      </h2>
      {!loading && teamFailed && (
        <p className="billing-footnote" role="status">
          Équipe non lisible : seules vos heures sont comptées.
        </p>
      )}
      {!loading && unreadable.length > 0 && (
        <p className="billing-footnote" role="status">
          Heures non lisibles pour {unreadable.join(", ")}.
        </p>
      )}
      {loading ? (
        <div className="empty-state" role="status">
          Chargement…
        </div>
      ) : failed ? (
        <div className="empty-state" role="alert">
          <div className="title">
            Impossible de charger les heures du projet.
          </div>
          <button
            type="button"
            className="pill-btn billing-retry"
            onClick={retry}
          >
            Réessayer
          </button>
        </div>
      ) : bilan.quarters === 0 ? (
        <div className="empty-state">
          Aucune heure observée sur ce projet ces {BILAN_WEEKS} dernières
          semaines.
        </div>
      ) : (
        <>
          <dl className="billing-total">
            <div>
              <dt>
                {teamView ? "Heures de l'équipe" : "Vos heures"}, {BILAN_WEEKS}{" "}
                semaines
              </dt>
              <dd>{formatQuarters(bilan.quarters)}</dd>
            </div>
          </dl>

          <WeekChart
            weeks={bilan.weeks}
            series={series}
            total={bilan.quarters}
          />

          <div className="billing-tablewrap">
            <table className="billing-table bilan-table">
              <caption className="billing-caption">Chantiers du projet</caption>
              <thead>
                <tr>
                  <th scope="col">Chantier</th>
                  <th scope="col">Qui</th>
                  <th scope="col" className="num">
                    Temps passé
                  </th>
                  <th scope="col">Début</th>
                  <th scope="col">Mis à jour</th>
                </tr>
              </thead>
              <tbody>
                {bilan.chantiers.map((c) => (
                  <tr
                    key={c.key}
                    className={c.stale ? "bilan-stale" : undefined}
                  >
                    <th scope="row">
                      {c.name}
                      <PrLinks prs={c.prs} />
                      {c.key.startsWith("c:") && (
                        <small className="chantiers-sub">{c.label}</small>
                      )}
                    </th>
                    <td>{whoText(c.people)}</td>
                    <td className="num">{formatQuarters(c.quarters)}</td>
                    <td>{c.span ? dateLabel(c.span.first) : ""}</td>
                    <td>
                      {c.span ? dateLabel(c.span.last) : ""}
                      {c.stale && c.span && (
                        <small className="chantiers-sub">
                          Sans activité depuis {daysBetween(c.span.last, today)}{" "}
                          jours
                        </small>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="billing-tablewrap">
            <table className="billing-table">
              <caption className="billing-caption">
                Temps passé par semaine
              </caption>
              <thead>
                <tr>
                  <th scope="col">Semaine et chantier</th>
                  <th scope="col">Qui</th>
                  <th scope="col" className="num">
                    Heures
                  </th>
                </tr>
              </thead>
              {recent.map((w) => {
                const prs = [
                  ...new Map(
                    w.lines
                      .flatMap((l) => l.prs)
                      .map((pr) => [`${pr.repo}#${pr.number}`, pr]),
                  ).values(),
                ];
                return (
                  <tbody key={w.monday}>
                    <tr className="lvl-client">
                      <th scope="rowgroup" colSpan={2}>
                        {w.label}
                      </th>
                      <td className="num">{formatQuarters(w.quarters)}</td>
                    </tr>
                    {w.lines.map((l) => (
                      <tr key={l.key} className="lvl-item">
                        <th scope="row">{lineName(l)}</th>
                        <td>{whoText(l.people)}</td>
                        <td className="num">{formatQuarters(l.quarters)}</td>
                      </tr>
                    ))}
                    {prs.length > 0 && (
                      <tr className="lvl-item lvl-action">
                        <td colSpan={3}>
                          Pull requests
                          <PrLinks prs={prs} />
                        </td>
                      </tr>
                    )}
                  </tbody>
                );
              })}
            </table>
          </div>
        </>
      )}
    </section>
  );
}

export default ProjectBilan;
